import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Pencil, AlertCircle, Calendar, Package, ArrowRightLeft } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardDescription, CardTitle } from '@/components/Card';
import { Button } from '@/components/Button';
import { StatusPill } from '@/components/StatusPill';
import { EmptyState, Skeleton } from '@/components/EmptyState';
import { DataTable } from '@/components/DataTable';
import { voucherHref } from '@/lib/voucherRoute';
import type { ColumnDef } from '@tanstack/react-table';
import { Timeline } from '@/components/Timeline';
import { ContactsPanel } from '@/components/ContactsPanel';
import { NotesPanel } from '@/components/NotesPanel';
import { TasksPanel } from '@/components/TasksPanel';
import { ActivityPanel } from '@/components/ActivityPanel';
import { CommentsPanel } from '@/components/CommentsPanel';
import { RelatedDocsPanel } from '@/components/RelatedDocsPanel';
import { ProjectProgressPanel } from '@/components/ProjectProgressPanel';
import { ProjectPurchaseOrdersPanel } from '@/components/ProjectPurchaseOrdersPanel';
import { api } from '@/lib/api';
import { money, date, qty, humanizeEnum } from '@/lib/format';
import { cn } from '@/lib/cn';
import type { DoctypeConfig } from '@/lib/doctypes';
import type { CreateSchema, FieldDef } from '@/lib/createSchema';

/**
 * Generic doctype detail view — ERPNext-style read-only page driven by the
 * DoctypeConfig + the existing create schema (we reuse the field list with
 * its labels and kinds so each doctype's detail rendering is correct
 * without a separate schema).
 *
 * Layout: two columns on wide screens.
 *   Left  — field key/value rows grouped into one card per ~8 fields.
 *   Right — meta strip (created/updated dates) + status pill + actions.
 *
 * For bespoke doctypes (sales_invoice, journal_entry, purchase_invoice) the
 * detail page is the form in read-only mode — they get their own routes and
 * aren't routed through here.
 */

export interface DetailViewProps {
  config: DoctypeConfig;
  schema: CreateSchema;
}

export function DetailView({ config, schema }: DetailViewProps) {
  const { id } = useParams({ strict: false }) as { id?: string };

  const { data, isLoading, error } = useQuery({
    queryKey: ['doctype-detail', config.endpoint, id],
    queryFn:  () => api<Record<string, unknown>>(`${config.endpoint}/${id}`),
    enabled:  !!id,
    retry: (count, err) => {
      const s = (err as { status?: number })?.status ?? 0;
      if (s === 401 || s === 403 || s === 404) return false;
      return count < 2;
    },
  });
  const forbidden = (error as { status?: number })?.status === 403;
  const notFound  = (error as { status?: number })?.status === 404;

  const listPath = `${config.modulePath}/${config.slug}`;
  const recordTitle = data ? pickTitle(data, config) : '…';

  return (
    <>
      <PageHeader
        crumbs={[
          { label: config.module, to: config.modulePath },
          { label: config.title, to: listPath },
        ]}
        title={recordTitle}
        status={renderDocstatus(data)}
        actions={
          <>
            <Button variant="ghost" asChild>
              <Link to={listPath as never}>
                <ArrowLeft className="size-4" /> Back to {config.title}
              </Link>
            </Button>
            <Button variant="secondary" asChild>
              <Link to={`${listPath}/${id}/edit` as never}>
                <Pencil className="size-4" /> Edit
              </Link>
            </Button>
          </>
        }
      />

      <div className="flex-1 px-4 sm:px-6 lg:px-8 pt-6 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr,320px] gap-4 max-w-[1400px]">
          <div className="space-y-4 min-w-0">
            {error && (
              <Card className="!p-3 bg-brand-error/5 border-brand-error/30 flex items-start gap-2">
                <AlertCircle className="size-4 text-brand-error mt-0.5 shrink-0" />
                <span className="text-body-sm text-brand-error">
                  {forbidden ? "You don't have access to this menu."
                    : notFound  ? "Document not found."
                    : (error as Error).message}
                </span>
              </Card>
            )}

            {isLoading ? (
              <Card><Skeleton className="h-64 w-full" /></Card>
            ) : data ? (
              <FieldsCard fields={schema.fields} record={data} />
            ) : null}

            {/* Child tables: render any array-of-objects field as a flat table. */}
            {data && renderChildTables(data, config.doctype)}

            {/* CRM extras: Contacts + Notes panels where the backend
                allowlists accept the parent doctype. */}
            {data && id && contactsAllowedFor(config.doctype) && (
              <ContactsPanel parentDoctype={config.doctype} parentID={id} />
            )}
            {data && id && config.doctype === 'warehouse' && (
              <WarehouseStock warehouseID={id} />
            )}
            {data && id && config.doctype === 'project' && (
              <>
                <ProjectProgressPanel projectID={id} />
                <ProjectPurchaseOrdersPanel projectID={id} />
                <TasksPanel projectID={id} />
              </>
            )}
            {data && id && tasksAllowedFor(config.doctype) && (
              <TasksPanel parentDoctype={config.doctype} parentID={id} />
            )}
            {data && id && activityAllowedFor(config.doctype) && (
              <ActivityPanel parentDoctype={config.doctype} parentID={id} />
            )}
            {data && id && notesAllowedFor(config.doctype) && (
              <NotesPanel parentDoctype={config.doctype} parentID={id} />
            )}
            {data && id && (
              <RelatedDocsPanel doctype={config.doctype} documentID={id} />
            )}
            {data && id && (
              <CommentsPanel doctype={config.doctype} documentID={id} />
            )}
          </div>

          {/* Right rail: meta + raw payload viewer + activity timeline */}
          {data && (
            <div className="space-y-4">
              <MetaRail record={data} />
              {id && <Timeline hideViews doctype={config.doctype} documentId={id} />}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

/* ---------- helpers ---------- */

// Mirrors the parentAllowlist on the backend (internal/crm/contact +
// internal/crm/note). Kept here so the FE can avoid rendering empty
// panels for doctypes the backend will reject.
const CONTACTS_ALLOWED = new Set(['customer', 'supplier', 'lead']);
const NOTES_ALLOWED    = new Set([
  'customer', 'supplier', 'lead', 'contact', 'opportunity',
  'asset', 'purchase_order', 'sales_invoice', 'purchase_invoice',
]);
const TASKS_ALLOWED    = new Set([
  'customer', 'supplier', 'lead', 'contact', 'opportunity',
  'asset', 'purchase_order', 'sales_invoice', 'purchase_invoice',
]);
const ACTIVITY_ALLOWED = TASKS_ALLOWED; // same allowlist for now

function contactsAllowedFor(doctype: string): boolean { return CONTACTS_ALLOWED.has(doctype); }
function notesAllowedFor(doctype: string): boolean    { return NOTES_ALLOWED.has(doctype); }
function tasksAllowedFor(doctype: string): boolean    { return TASKS_ALLOWED.has(doctype); }
function activityAllowedFor(doctype: string): boolean { return ACTIVITY_ALLOWED.has(doctype); }


function FieldsCard({ fields, record }: { fields: FieldDef[]; record: Record<string, unknown> }) {
  return (
    <Card>
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
        {fields.map((f) => (
          <DetailRow key={f.name} field={f} value={record[f.name]} record={record} className={f.span === 2 ? 'sm:col-span-2' : ''} />
        ))}
      </div>
    </Card>
  );
}

function DetailRow({ field, value, record, className }: { field: FieldDef; value: unknown; record?: Record<string, unknown>; className?: string }) {
  return (
    <div className={cn('space-y-0.5', className)}>
      <div className="text-micro-uppercase text-stone">{field.label}</div>
      <div className="text-body-sm text-ink break-words">{formatValue(field, value, record)}</div>
    </div>
  );
}

function formatValue(field: FieldDef, v: unknown, record?: Record<string, unknown>): React.ReactNode {
  if (v === null || v === undefined || v === '') return <span className="text-stone">—</span>;
  switch (field.kind) {
    case 'money':
      return <span className="num">{money(String(v))}</span>;
    case 'number':
      return <span className="num">{String(v)}</span>;
    case 'date':
      return <span className="num">{date(String(v))}</span>;
    case 'bool':
      return v ? <StatusPill tone="success" withDot={false}>Yes</StatusPill>
               : <StatusPill tone="neutral" withDot={false}>No</StatusPill>;
    case 'textarea':
      return <span className="whitespace-pre-wrap">{String(v)}</span>;
    case 'link':
      return <LinkValue field={field} value={v} record={record} />;
    case 'select': {
      // Prefer the schema's human label for the enum value (e.g. gender
      // 'male' → 'Male', depreciation_method 'straight_line' → 'Straight line');
      // fall back to humanizing the raw token so we never leak snake_case.
      const opt = field.options?.find((o) => o.value === String(v));
      return <span>{opt ? opt.label : humanizeEnum(String(v))}</span>;
    }
    default:
      return <span>{String(v)}</span>;
  }
}

// Looks like `pre_01ABC...` — UID-prefixed identifier we should resolve.
const UID_RE = /^[a-z]{2,10}_[0-9A-Z]{20,}$/;

/**
 * Renders a link-kind field with the best human label we can find:
 *   1. A denormalized companion field on the record (`*_name`, `*_display_name`, `*_code`).
 *   2. The labeled property on the linked record, fetched on demand.
 *   3. As a last resort, the raw UID (mono).
 *
 * When the field has `linkSwitch`, the endpoint/label is chosen from the
 * trigger field's value on the record (mirrors LinkPicker in CreateFormPage).
 *
 * The label is wrapped in a `<Link>` to the linked detail page when we know
 * the endpoint, so the user can navigate from one record to another.
 */
function LinkValue({ field, value, record }: { field: FieldDef; value: unknown; record?: Record<string, unknown> }) {
  const id = String(value ?? '');

  // Resolve effective endpoint + label from linkSwitch if applicable.
  let endpoint = field.linkEndpoint;
  let label    = field.linkLabel;
  if (field.linkSwitch && record) {
    const trigger = record[field.linkSwitch.triggerField] as string | undefined;
    const cfg = trigger ? field.linkSwitch.byValue[trigger] : undefined;
    if (cfg) {
      endpoint = cfg.endpoint;
      label    = cfg.label ?? label;
    }
  }

  // 1. Companion field on the record.
  if (record) {
    const stem = field.name.replace(/_id$/, '');
    const candidates = stem === field.name
      ? [`${field.name}_name`, `${field.name}_display_name`, `${field.name}_code`]
      : [`${stem}_name`, `${stem}_display_name`, `${stem}_code`];
    for (const key of candidates) {
      const display = record[key];
      if (typeof display === 'string' && display) {
        return <ResolvedLink endpoint={endpoint} id={id} display={display} />;
      }
    }
  }

  // 2. If the value isn't a UID (e.g. a free-text lead source slug), show it as-is.
  if (!UID_RE.test(id)) {
    return <span className="text-charcoal">{id}</span>;
  }

  // 3. Fetch the linked record and display its labeled property.
  return <FetchedLinkValue id={id} endpoint={endpoint} label={label} />;
}

function FetchedLinkValue({ id, endpoint, label }: { id: string; endpoint?: string; label?: string }) {
  const enabled = !!endpoint && !!id;
  const { data } = useQuery({
    queryKey: ['detail-link', endpoint, id],
    queryFn:  () => api<Record<string, unknown>>(`${endpoint}/${id}`),
    enabled,
    staleTime: 60_000,
    retry: false,
  });
  if (!enabled) {
    return <span className="font-mono text-caption text-charcoal">{id}</span>;
  }
  // Prefer the configured label, then a sensible cascade of common names.
  const pick = (k: string) => (typeof data?.[k] === 'string' && data?.[k]) as string | undefined;
  const display =
    (label && pick(label)) ||
    pick('display_name') || pick('name') || pick('code') ||
    pick('subject') || pick('asset_name') || pick('project_name') ||
    pick('item_name') || pick('lead_name');
  if (display) return <ResolvedLink endpoint={endpoint} id={id} display={display} />;
  return <span className="font-mono text-caption text-charcoal">{id}</span>;
}

function ResolvedLink({ endpoint, id, display }: { endpoint?: string; id: string; display: string }) {
  if (!endpoint || !id) return <span className="text-charcoal">{display}</span>;
  return (
    <Link to={`${endpoint}/${id}` as never} className="text-brand-green-ink hover:underline">
      {display}
    </Link>
  );
}

function pickTitle(record: Record<string, unknown>, config: DoctypeConfig): string {
  // Prefer the most "human" field for the page title.
  for (const k of ['name', 'display_name', 'item_code', 'code', 'account_name', 'asset_name', 'subject']) {
    if (typeof record[k] === 'string' && record[k]) return record[k] as string;
  }
  return (record.id as string) ?? config.singular;
}

function renderDocstatus(record: Record<string, unknown> | undefined): React.ReactNode {
  const ds = record?.docstatus;
  if (ds === undefined || ds === null) return null;
  if (ds === 1) return <StatusPill tone="success">Submitted</StatusPill>;
  if (ds === 2) return <StatusPill tone="danger">Cancelled</StatusPill>;
  return <StatusPill tone="neutral">Draft</StatusPill>;
}

// Doctypes whose `defaults` child table is a raw internal FK map
// (company / income / expense / tax account IDs) — never customer-facing.
const HIDE_DEFAULTS_FOR = new Set(['item', 'customer', 'supplier']);
// Columns that only ever carry internal identifiers or dev-only indices.
const CHILD_COL_DENYLIST = new Set(['id', '$schema', 'row_index', 'posted_voucher_id']);
// A child table this large is capped in the detail view (e.g. a 96-row
// depreciation schedule) — the full set stays available on export.
const CHILD_ROW_CAP = 12;

function renderChildTables(record: Record<string, unknown>, doctype: string): React.ReactNode {
  // Any field whose value is an array of objects is treated as a child table.
  let tables = Object.entries(record).filter(([, v]) =>
    Array.isArray(v) && v.length > 0 && typeof v[0] === 'object' && v[0] !== null,
  ) as Array<[string, Record<string, unknown>[]]>;
  // Drop the raw internal "defaults" dump on master-data detail pages.
  if (HIDE_DEFAULTS_FOR.has(doctype)) {
    tables = tables.filter(([key]) => key !== 'defaults');
  }
  if (tables.length === 0) return null;
  return tables.map(([key, allRows]) => {
    const cap = allRows.length > CHILD_ROW_CAP ? CHILD_ROW_CAP : allRows.length;
    const rows = allRows.slice(0, cap);
    const hiddenCount = allRows.length - rows.length;
    // Hide denylisted columns and any column that carries an internal FK ULID.
    const columns = Object.keys(rows[0]!).filter(
      (k) => !CHILD_COL_DENYLIST.has(k)
          && rows[0]![k] !== null
          && typeof rows[0]![k] !== 'object'
          && !(typeof rows[0]![k] === 'string' && UID_RE.test(rows[0]![k] as string)),
    );
    return (
      <Card key={key} padded={false}>
        <div className="px-5 py-3 border-b border-hairline">
          <CardTitle>{prettifyKey(key)}</CardTitle>
          <CardDescription>{allRows.length} {allRows.length === 1 ? 'row' : 'rows'}</CardDescription>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-body-sm">
            <thead className="bg-surface-soft border-b border-hairline text-micro-uppercase text-stone">
              <tr>
                {columns.map((c) => (
                  <th key={c} className="text-left font-medium px-3 py-2">{prettifyKey(c)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-t border-hairline">
                  {columns.map((c) => (
                    <td key={c} className="px-3 py-2 text-charcoal align-top">
                      {formatCell(c, r[c])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {hiddenCount > 0 && (
          <div className="px-5 py-2.5 border-t border-hairline text-caption text-stone">
            + {hiddenCount} more {hiddenCount === 1 ? 'row' : 'rows'}
          </div>
        )}
      </Card>
    );
  });
}

function formatCell(key: string, v: unknown): React.ReactNode {
  if (v === null || v === undefined || v === '') return <span className="text-stone">—</span>;
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'number') return <span className="num">{v}</span>;
  // Heuristics for common keys
  if (/_amount$|_total$|amount$|rate$|^debit$|^credit$|^accumulated_|^opening_|^closing_|^book_value|^salvage|^cost$|^value$|^balance$/i.test(key)) {
    return <span className="num">{money(String(v))}</span>;
  }
  if (/_at$|_date$/.test(key)) {
    return <span className="num">{date(String(v))}</span>;
  }
  if (typeof v === 'string' && v.startsWith('acc_')) return <span className="font-mono text-caption">{v}</span>;
  return <span>{String(v)}</span>;
}

function prettifyKey(k: string): string {
  return k.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
}

function MetaRail({ record }: { record: Record<string, unknown> }) {
  // Raw-JSON panel is only useful to operators — gate it behind is_system so
  // regular users see a clean Meta card and nothing more.
  const created = record.created_at as string | undefined;
  const updated = record.updated_at as string | undefined;
  // Record info holds provenance only — the human-readable identity fields
  // (Name / Code / Type) already appear in the main Details card, so we don't
  // repeat them here.
  return (
    <div className="space-y-4">
      <Card>
        <CardTitle>Record info</CardTitle>
        <div className="mt-3 space-y-2 text-body-sm">
          {created && <Row label="Created" value={date(created)} icon={Calendar} />}
          {updated && <Row label="Updated" value={date(updated)} icon={Calendar} />}
        </div>
      </Card>
    </div>
  );
}

/** Humanize a snake_case token: "raw_material" → "Raw material". */
type StockContentRow = { item_id: string; code: string; name: string; uom: string; qty: string };
type StockMovementRow = {
  posting_date: string; item_id: string; item_code: string; item_name: string;
  qty_change: string; voucher_type: string; voucher_id: string; voucher_name: string;
};

function WarehouseStock({ warehouseID }: { warehouseID: string }) {
  const navigate = useNavigate();
  const { data: contents, isLoading: contentsLoading } = useQuery({
    queryKey: ['wh-contents', warehouseID],
    queryFn: () => api<{ items: StockContentRow[] }>(`/stock/warehouses/${warehouseID}/contents`),
  });
  const { data: moves, isLoading: movesLoading } = useQuery({
    queryKey: ['wh-movements', warehouseID],
    queryFn: () => api<{ items: StockMovementRow[] }>(`/stock/warehouses/${warehouseID}/movements`),
  });
  const items = contents?.items ?? [];
  const mv = moves?.items ?? [];

  const itemHref = (id: string) => `/accounting/items/${id}`;

  const stockColumns = useMemo<ColumnDef<StockContentRow>[]>(() => [
    {
      accessorKey: 'code', header: 'Code',
      cell: (info) => (
        <Link
          to={itemHref(info.row.original.item_id) as never}
          onClick={(e) => e.stopPropagation()}
          className="font-mono text-caption font-medium text-text-primary hover:text-accent hover:underline underline-offset-2"
        >
          {info.getValue<string>()}
        </Link>
      ),
    },
    { accessorKey: 'name', header: 'Name', cell: (info) => <span className="text-text-primary">{info.getValue<string>()}</span> },
    { accessorKey: 'qty', header: 'Qty', meta: { align: 'right' }, cell: (info) => <span className="num">{qty(info.getValue<string>())}</span> },
    { accessorKey: 'uom', header: 'UOM', cell: (info) => <span className="text-text-secondary">{info.getValue<string>()}</span> },
  ], []);

  const movementColumns = useMemo<ColumnDef<StockMovementRow>[]>(() => [
    { accessorKey: 'posting_date', header: 'Date', cell: (info) => <span className="text-text-secondary">{date(info.getValue<string>())}</span> },
    {
      accessorKey: 'item_code', header: 'Item',
      cell: (info) => {
        const row = info.row.original;
        return (
          <Link
            to={itemHref(row.item_id) as never}
            onClick={(e) => e.stopPropagation()}
            className="text-text-primary hover:text-accent hover:underline underline-offset-2"
          >
            {row.item_code}
          </Link>
        );
      },
    },
    {
      accessorKey: 'qty_change', header: 'Qty', meta: { align: 'right' },
      cell: (info) => {
        const v = info.getValue<string>();
        return <span className={'num ' + (v.startsWith('-') ? 'text-brand-error' : 'text-brand-success')}>{qty(v)}</span>;
      },
    },
    {
      accessorKey: 'voucher_name', header: 'Source document',
      cell: (info) => {
        const row = info.row.original;
        const label = row.voucher_type ? row.voucher_type + ' · ' + row.voucher_name : '—';
        const href = voucherHref(row.voucher_type, row.voucher_id);
        if (href) {
          return (
            <Link
              to={href as never}
              onClick={(e) => e.stopPropagation()}
              className="text-caption text-text-primary hover:text-accent hover:underline underline-offset-2"
            >
              {label}
            </Link>
          );
        }
        return <span className="text-caption text-text-secondary">{label}</span>;
      },
    },
  ], []);

  return (
    <>
      <Card padded={false}>
        <div className="px-5 py-3 border-b border-hairline">
          <CardTitle><Package className="size-4 inline mr-1.5 text-accent" /> Current stock</CardTitle>
          <CardDescription>Items currently held in this warehouse.</CardDescription>
        </div>
        <DataTable
          columns={stockColumns}
          data={items}
          loading={contentsLoading}
          searchPlaceholder="Search items…"
          onRowClick={(row) => navigate({ to: itemHref(row.item_id) as never })}
          emptyState={<EmptyState icon={Package} title="No stock on hand" description="This warehouse currently holds no items." />}
        />
      </Card>
      <Card padded={false}>
        <div className="px-5 py-3 border-b border-hairline">
          <CardTitle><ArrowRightLeft className="size-4 inline mr-1.5 text-accent" /> Stock movements</CardTitle>
          <CardDescription>Goods in/out of this warehouse, linked to the source document (Mutasi / Goods Received / Goods Issued).</CardDescription>
        </div>
        <DataTable
          columns={movementColumns}
          data={mv}
          loading={movesLoading}
          searchPlaceholder="Search movements…"
          emptyState={<EmptyState icon={ArrowRightLeft} title="No movements yet" description="No goods have moved in or out of this warehouse." />}
        />
      </Card>
    </>
  );
}

function Row({ label, value, mono, icon: Icon }: { label: string; value: string; mono?: boolean; icon?: React.ComponentType<{ className?: string }> }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-stone inline-flex items-center gap-1.5">{Icon ? <Icon className="size-3" /> : null}{label}</span>
      <span className={'text-right ' + (mono ? 'font-mono text-caption text-charcoal' : 'text-ink')}>{value}</span>
    </div>
  );
}
